<template>
    <DModal
        v-if="showModal"
        :title="modalHeader"
        is-big
        :confirm-close="formDataUpdated"
        :confirm-close-text="__('customers.contracts.discard_changes')"
        :show="showModal"
        @close="closeModal"
        @dismis="closeModal"
    >
        <div class="table-padding">
            <DTable
                :fields="fields"
                :items="tableItems"
            >
                <template #contractTerm="{item}">
                    <td :class="{'text-warning' : item.contractTermUpdated}">
                        {{ item.contractTerm }}
                    </td>
                </template>
                <template #noticePeriod="{item}">
                    <td :class="{'text-warning' : item.noticePeriodUpdated}">
                        {{ item.noticePeriod }}
                    </td>
                </template>
                <template #startDate="{item}">
                    <td>
                        {{ item.startDate }}
                    </td>
                </template>
                <template #endDate="{item}">
                    <td>
                        {{ item.endDate }}
                    </td>
                </template>
                <template #action="{item}">
                    <td>
                        <DButton
                            v-if="canBeDeleted(item) === true && previewMode === false"
                            :id="`btn-delete-${item.id}`"
                            icon="trash-can-outline"
                            color="danger"
                            mini
                            @click="deletePhase(item)"
                        />
                    </td>
                </template>
            </DTable>
        </div>

        <div
            v-if="deletionDataUpdated === false"
            class="table-padding"
        >
            <h4 class="pb-2">
                {{ __('customers.contracts.runtime_overview_form.header') }}
            </h4>

            <DForm @submit.prevent="">
                <!-- Contract term -->
                <DFormRow
                    :label="__('customers.contracts.runtime_overview_form.contract_term')"
                    :is-required="true"
                >
                    <div class="row">
                        <div class="col-8">
                            <DInput
                                id="contract_term"
                                v-model="form.contract_term"
                                :disabled="previewMode"
                                @change="updateFormValues"
                            />
                        </div>
                        <div class="col-4">
                            <DSelect
                                id="contract_term_unit"
                                v-model="form.contract_term_unit"
                                :options="units"
                                :disabled="previewMode"
                                @change="updateFormValues"
                            />
                        </div>
                    </div>
                    <p
                        v-if="errors.contract_term"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.contract_term[0] }}
                    </p>
                    <p
                        v-if="errors.contract_term_unit"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.contract_term_unit[0] }}
                    </p>
                </DFormRow>

                <!-- Notice period -->
                <DFormRow
                    :label="__('customers.contracts.runtime_overview_form.notice_period')"
                    :is-required="true"
                >
                    <div class="row">
                        <div class="col-8">
                            <DInput
                                id="notice_period"
                                v-model="form.notice_period"
                                :disabled="previewMode"
                                @change="updateFormValues"
                            />
                        </div>
                        <div class="col-4">
                            <DSelect
                                id="notice_period_unit"
                                v-model="form.notice_period_unit"
                                :options="units"
                                :disabled="previewMode"
                                @change="updateFormValues"
                            />
                        </div>
                    </div>
                    <p
                        v-if="errors.notice_period"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.notice_period[0] }}
                    </p>
                    <p
                        v-if="errors.notice_period_unit"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.notice_period_unit[0] }}
                    </p>
                </DFormRow>

                <!-- Start date -->
                <DFormRow
                    :label="__('customers.contracts.runtime_overview_form.start_date')"
                    :is-required="true"
                >
                    <DDateFormatInput
                        id="start_date"
                        v-model="form.start_date"
                        format="YYYY-MM-DD"
                        :disabled="previewMode"
                        type="date"
                        @valueChanged="updateFormValues"
                    />
                    <p
                        v-if="errors.start_date"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.start_date[0] }}
                    </p>
                </DFormRow>

                <!-- Overwrite planed runtime settings -->
                <DFormRow
                    :label="__('customers.contracts.runtime_overview_form.overwrite_planned_runtime')"
                    :is-required="false"
                >
                    <DFormCheck
                        id="overwrite"
                        v-model="form.overwrite_planned_runtime"
                        :disabled="previewMode"
                        class="overwrite-checkbox"
                    />
                    <p
                        v-if="errors.overwrite_planned_runtime"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.overwrite_planned_runtime[0] }}
                    </p>
                </DFormRow>
                <DFormRow
                    :label="__('customers.contracts.phase_text')"
                >
                    <DInput
                        id="phase_text"
                        v-model="form.phase_text"
                        :disabled="previewMode"
                        @valueChanged="updateFormValues"
                    />
                    <p
                        v-if="errors.phase_text"
                        class="m-0 pt-1 text-danger"
                    >
                        {{ errors.phase_text[0] }}
                    </p>
                </DFormRow>
            </DForm>
        </div>
        <div class="table-padding pt-3 pb-3 d-flex gap-2 text-right">
            <div
                v-if="(formDataUpdated || deletionDataUpdated) && previewMode === true"
                class="width-100"
            >
                <DInput
                    id="change_reason"
                    v-model="form.activityLogComment"
                    class="d-inline-block"
                    :placeholder="__('messages.change_reason')"
                    add-input-classes="bg-light"
                    :is-valid="errors.activity_log_comment !== undefined ? false : null"
                    :invalid-feedback="errors.activity_log_comment !== undefined ? errors.activity_log_comment[0] : null"
                    maxlength="1000"
                />
                <div
                    v-if="incidents.length > 1 && userPermissions.includes('customer.incidents_view') && userPermissions.includes('customer.incidents_manage')"
                    class="incident-class"
                >
                    <span>{{ __('customers.business_incidents.add_to_incident') }}</span>
                    <DSelect
                        id="add_incident"
                        v-model="form.incident_id"
                        :options="incidents"
                        :is-valid="isFormFieldValid('incident_id')"
                    />
                </div>
                <p
                    v-if="errors.incident_id"
                    class="error-msg"
                >
                    {{ errors.incident_id }}
                </p>
            </div>
            <DButton
                v-if="formDataUpdated && deletionDataUpdated === false"
                :label="showPreviewLabel"
                :color="'primary'"
                :loading="isLoading"
                :disabled="isLoading"
                @click="getRuntimeOverviewData"
            />
            <DButton
                v-if="deletionDataUpdated"
                :label="showPreviewLabel"
                :color="'primary'"
                :loading="isLoading"
                :disabled="isLoading"
                @click="getRuntimeOverviewDataBeforeDeletion"
            />
            <DButton
                v-if="previewMode || deletionDataUpdated === true"
                :label="__('customers.contracts.decline_changes')"
                :color="'danger'"
                :loading="isLoading"
                :disabled="isLoading"
                @click="resetRuntimeOverview"
            />
        </div>
    </DModal>
</template>

<script>

export default {
    name: 'RuntimeOverviewModal',
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        groupedContractPhases: {
            type: Array,
            default: () => { return [] }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        units: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['close-modal'],
    data () {
        return {
            fields: [
                { key: 'startDate', label: this.__('customers.contracts.runtime_overview_table.from') },
                { key: 'endDate', label: this.__('customers.contracts.runtime_overview_table.to') },
                { key: 'contractTerm', label: this.__('customers.contracts.runtime_overview_table.contract_term') },
                { key: 'noticePeriod', label: this.__('customers.contracts.runtime_overview_table.notice_period') },
                { key: 'phase_text', label: this.__('customers.contracts.phase_text') },
                { key: 'action', label: '' }
            ],
            form: {
                id: null,
                contract_id: null,
                start_date: null,
                contract_term: null,
                contract_term_unit: null,
                notice_period: null,
                notice_period_unit: null,
                overwrite_planned_runtime: null,
                activityLogComment: '',
                incident_id: null,
                phase_text: null
            },
            tableItems: [],
            errors: {},
            previewMode: false,
            formDataUpdated: false,
            oldTableItems: [],
            rawTableItems: [],
            isLoading: false,
            incidents: [],
            deletionDataUpdated: false,
            phaseForDeletion: null
        }
    },
    computed: {
        modalHeader () {
            return this.__('customers.contracts.runtime_overview_header') + this.contract.id
        },
        activeContractPhase () {
            return this.contract.active_contract_phase
        },
        showPreviewLabel () {
            if (!this.previewMode) return this.__('customers.contracts.runtime_preview_mode')
            return this.__('customers.contracts.accept_changes')
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    created () {
        this.rawTableItems = this.groupedContractPhases
        this.updateTableItems()
    },
    mounted () {
        this.fillDefaultFormData()
        if (this.contract.customer_id && this.userPermissions.includes('customer.incidents_view') && this.userPermissions.includes('customer.incidents_manage')) {
            window.axios.get(this.$route('customers.customers.get-customer-open-business-incidents-array', this.contract.customer_id))
                .then(response => {
                    this.incidents = response.data.incidents
                })
        }
    },
    methods: {
        fillDefaultFormData () {
            this.form.id = this.activeContractPhase.id
            this.form.start_date = this.initialStartDate()
            this.form.contract_term = this.activeContractPhase.contract_term
            this.form.contract_term_unit = this.activeContractPhase.contract_term_unit
            this.form.notice_period = this.activeContractPhase.notice_period
            this.form.notice_period_unit = this.activeContractPhase.notice_period_unit
            this.form.overwrite_planned_runtime = false
            this.form.phase_text = this.activeContractPhase.phase_text
        },
        initialStartDate () {
            let startDate
            if (this.contract.billed_until_date) {
                startDate = new Date(this.contract.billed_until_date)
                startDate.setDate(startDate.getDate() + 1)
            } else {
                startDate = new Date()
            }

            return startDate.toISOString().split('T')[0]
        },
        updateTableItems () {
            const items = []
            for (const groupLine of this.rawTableItems) {
                if (groupLine.contractPhaseIds.includes(this.activeContractPhase.id)) {
                    Object.assign(groupLine, { _classes: 'table-success' })
                }
                items.push(groupLine)
            }

            this.tableItems = items
        },
        closeModal () {
            this.fillDefaultFormData()
            this.previewMode = false
            this.errors = {}
            this.form.activityLogComment = ''
            this.formDataUpdated = false
            this.$emit('close-modal')
        },
        updateFormValues () {
            this.formDataUpdated = this.checkIfFormIsUpdated()
        },
        resetRuntimeOverview () {
            this.previewMode = false
            this.rawTableItems = this.oldTableItems
            this.formDataUpdated = false
            this.deletionDataUpdated = false
            this.updateTableItems()
        },
        checkIfFormIsUpdated () {
            return this.form.start_date !== this.activeContractPhase.start_date ||
            this.form.contract_term !== this.activeContractPhase.contract_term ||
            this.form.contract_term_unit !== this.activeContractPhase.contract_term_unit ||
            this.form.notice_period !== this.activeContractPhase.notice_period ||
            this.form.notice_period_unit !== this.activeContractPhase.notice_period_unit
        },
        getRuntimeOverviewData () {
            this.isLoading = true
            this.errors = {}
            window.axios.post(this.$route('customers.customers.contracts.runtime_overview', {
                customer: this.contract.customer_id,
                contract: this.contract
            }), {
                recalculate: 1,
                start_date: this.form.start_date,
                contract_term: this.form.contract_term,
                contract_term_unit: this.form.contract_term_unit,
                notice_period: this.form.notice_period,
                notice_period_unit: this.form.notice_period_unit,
                overwrite_planned_runtime: this.form.overwrite_planned_runtime,
                save_phase: this.previewMode,
                activity_log_comment: this.form.activityLogComment,
                incident_id: this.form.incident_id,
                phase_text: this.form.phase_text
            })
                .then((response) => {
                    if (!this.previewMode) {
                        this.oldTableItems = this.rawTableItems
                    }

                    if (this.previewMode === true) {
                        this.$inertia.reload()
                        this.formDataUpdated = false
                    }

                    this.previewMode = !this.previewMode
                    this.rawTableItems = response.data.groupedContractPhases
                    this.updateTableItems()
                })
                .catch((error) => {
                    if (error && error.response && error.response.data) {
                        this.errors = error.response.data.errors
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        parseToFloat (number) {
            return Number(number).toFixed(2)
        },
        canBeDeleted (item) {
            if (this.activeContractPhase.id === item.id) {
                return false
            }

            if (new Date(this.activeContractPhase.start_date) > new Date(item.startDate)) {
                return false
            }

            return true
        },
        deletePhase (item) {
            if (item.id !== null) {
                this.deletionDataUpdated = true
                this.phaseForDeletion = item.id
            }

            this.getRuntimeOverviewDataBeforeDeletion()
        },
        getRuntimeOverviewDataBeforeDeletion () {
            this.showWarningMessage = false
            this.isLoading = true
            this.errors = {}
            window.axios.post(this.$route('customers.customers.contracts.runtime_deletion_overview', {
                customer: this.contract.customer_id,
                contract: this.contract
            }), {
                delete: this.previewMode,
                activity_log_comment: this.form.activityLogComment,
                incident_id: this.form.incident_id,
                phase_for_deletion: this.phaseForDeletion
            })
                .then((response) => {
                    if (!this.previewMode) {
                        this.oldTableItems = this.rawTableItems
                    }
                    if (this.previewMode) {
                        this.phaseForDeletion = null
                        this.deletionDataUpdated = false
                    }
                    this.previewMode = !this.previewMode
                    this.rawTableItems = response.data.groupedContractPhases

                    this.updateTableItems()
                })
                .catch((error) => {
                    if (error && error.response && error.response.data) {
                        this.errors = error.response.data.errors
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    }
}
</script>
<style lang="scss">
.table-padding {
    padding-left: 20px;
    padding-right: 20px;
}
.vertical-padding {
    padding-top: 40px;
    padding-bottom: 20px;
}
.bottom-padding {
    padding-bottom: 20px;
    padding-left: 20px;
    padding-right: 20px;
}
.overwrite-checkbox input {
    position: relative !important;
}
.width-100 {
    width: 100%;
}
</style>
